import { useMe } from "@bastion/data";
import { useAuthStore } from "@/shared/stores/authStore";
import type { OrgRendering } from "./navigation";

/**
 * The caller's organization, in the shape the navigation helpers read.
 *
 * Every shell surface that hides a Parametric-only product reads it from here,
 * so the user id that keys the `/me` cache is wired in one place. A call site
 * that passed no id would get a disabled query, an undefined answer, and a
 * shell that silently renders every product.
 *
 * Returns `null` while the query loads, when it fails, and before sign-in.
 * `visibleProductTabs` and `homeLandingPath` read all three as "leave the UI
 * open".
 *
 * This wiring survives mutation untested, by decision. The webapp vitest suite
 * runs in the node environment and has no `@testing-library/react` (see the
 * root `CLAUDE.md`), so a hook that reads a store and a query cannot be
 * rendered here. The helpers underneath it are pinned by
 * `app/navigation.test.ts` and `app/layout/commandPaletteCommands.test.ts`, and
 * the seam itself is covered by the two Playwright tests in
 * `e2e/authz.spec.ts` once the `unitzero.ai` user is provisioned on dev. Move
 * this to a rendered test if the webapp ever gains a DOM environment.
 */
export function useOrgRendering(): OrgRendering {
  const userId = useAuthStore((state) => state.user?.id);
  const { data } = useMe(userId);
  return data ?? null;
}
